import _ from 'lodash'
import { named, optional, withDependencies } from '@wix/thunderbolt-ioc'
import {
	BrowserWindow,
	BrowserWindowSymbol,
	CompEventsRegistrarSym,
	Experiments,
	ExperimentsSymbol,
	IAppDidLoadPageHandler,
	ICompEventsRegistrar,
	IPageDidMountHandler,
	IPageDidUnmountHandler,
	IPageWillMountHandler,
	IPageWillUnmountHandler,
	IPropsStore,
	PageFeatureConfigSymbol,
	pageIdSym,
	Props,
} from '@wix/thunderbolt-symbols'
import {
	name as featureName,
	ReactionCreatorFactorySymbol,
	scrubReactionsManagerSymbol,
	ShouldEnableTriggersAndReactionsSymbol,
	triggersBreakpointValidatorSymbol,
	ViewportIntersectionHandlerSymbol,
} from './symbols'
import { hasAncestorWithId } from './utils'
import { TriggerTypeEventNameMapper } from './constants'
import type {
	IReactionCreatorFactory,
	TargetCompIdReactions,
	TriggersAndReactionsPageConfig,
	IViewportIntersectionHandler,
	ITriggersBreakpointValidator,
	IShouldEnableTriggersAndReactions,
	IScrubReactionManager,
	RegisterTriggerFunctionParams,
} from './types'
import { getFullId, REPEATER_DELIMITER, getItemId } from '@wix/thunderbolt-commons'
import { IMotionEffectsInit, MotionEffectsInitSymbol, MotionEffectsManager } from 'feature-motion-effects'
import { ILightbox, LightboxSymbol } from 'feature-lightbox'

const eventOptions = { addCompId: true }
const triggersAndReactionsFactory = (
	{
		compsToTriggers,
		isTouchDevice,
		compIdsWithAccessibleTrigger,
		effectTriggerToReaction,
	}: TriggersAndReactionsPageConfig,
	reactionCreatorFactory: IReactionCreatorFactory,
	compEventsRegistrar: ICompEventsRegistrar,
	browserWindow: NonNullable<BrowserWindow>,
	pageIdSymbol: string,
	props: IPropsStore,
	viewportIntersectionHandler: IViewportIntersectionHandler,
	{ isTriggerBpRangeInCurrentBreakpoint }: ITriggersBreakpointValidator,
	scrubEffectsManager: IScrubReactionManager,
	{ shouldEnableTriggersAndReactions }: IShouldEnableTriggersAndReactions,
	experiments: Experiments,
	lightboxAPI?: ILightbox,
	motionEffects?: IMotionEffectsInit
): IPageWillMountHandler &
	IPageDidMountHandler &
	IPageDidUnmountHandler &
	IAppDidLoadPageHandler &
	IPageWillUnmountHandler => {
	let motionEffectsManager: MotionEffectsManager | undefined

	const shouldEnableAccessibilityTriggers = compIdsWithAccessibleTrigger.length > 0

	const registerCommonTrigger = ({ compToRegister, triggerData, triggerType }: RegisterTriggerFunctionParams) => {
		compEventsRegistrar.register(
			compToRegister,
			TriggerTypeEventNameMapper[triggerType],
			reactionCreatorFactory.createReaction(triggerData, compToRegister),
			eventOptions
		)
	}

	const registerAnimationTrigger = ({ compToRegister, triggerData, triggerType }: RegisterTriggerFunctionParams) => {
		const triggerEffectToReactionData: Record<string, TargetCompIdReactions> = {}
		_.reduce(
			triggerData,
			(acc, reactionsData, targetCompId) => {
				reactionsData.forEach((reactionData) => {
					const currentBpRange = reactionData.triggerBpRange
					reactionData.reactions.forEach((reaction) => {
						const id = reaction.reactionData.name
							? reaction.reactionData.name
							: reaction.reactionData.effect
						_.forEach(effectTriggerToReaction, (reactionsIds, trigger) => {
							if (reactionsIds.includes(`#${id}`)) {
								acc[trigger] = acc[trigger] || {}
								acc[trigger][targetCompId] = acc[trigger][targetCompId] || []

								acc[trigger][targetCompId] = [
									...acc[trigger][targetCompId],
									{
										triggerBpRange: { ...currentBpRange },
										reactions: [reaction],
									},
								]
							}
						})
					})
				})
				return acc
			},
			triggerEffectToReactionData
		)

		_.forEach(triggerEffectToReactionData, (data, effectId) => {
			motionEffectsManager?.addEffectCallback(
				effectId,
				triggerType,
				reactionCreatorFactory.createReaction(data, compToRegister)
			)
		})
	}

	const registerHoverTrigger = ({ compToRegister, triggerData, pageId }: RegisterTriggerFunctionParams) => {
		if (isTouchDevice) {
			let outerClickRegistration: Function | null
			const registerOuterClickCallback = (originalSrcCompId: string) => {
				const reverseReaction = (event: Event) => {
					const shouldPropagateEvent =
						experiments['specs.thunderbolt.propagate_event_mobile_hover_interaction']
					if (!shouldPropagateEvent) {
						event.stopPropagation()
					}

					if (hasAncestorWithId(event.target, compToRegister)) {
						return
					}
					// @ts-ignore
					event.compId = originalSrcCompId // we pass the original compId in order to be able reverse correct item in repeater
					reactionCreatorFactory.createReverseReaction(triggerData, compToRegister)(event)
					outerClickRegistration && compEventsRegistrar.unregister(pageId, 'onClick', outerClickRegistration)
					outerClickRegistration = null
				}
				setTimeout(() => {
					// we need to register only on next tick to prevent the eventsRegistrar from triggering the reverse reaction
					// immediately when the first reaction is invoked.
					outerClickRegistration = compEventsRegistrar.register(
						pageId,
						'onClick',
						reverseReaction,
						eventOptions
					)
				}, 0)
			}
			compEventsRegistrar.register(
				compToRegister,
				'onClick',
				reactionCreatorFactory.createReaction(triggerData, compToRegister, registerOuterClickCallback),
				eventOptions
			)
		} else {
			compEventsRegistrar.register(
				compToRegister,
				'onMouseEnter',
				reactionCreatorFactory.createReaction(triggerData, compToRegister),
				eventOptions
			)
			compEventsRegistrar.register(
				compToRegister,
				'onMouseLeave',
				reactionCreatorFactory.createReverseReaction(triggerData, compToRegister),
				eventOptions
			)
		}
	}
	let activeElements: Array<any> = []
	const focusInHandler = (event: FocusEvent) => {
		const target = event.target as HTMLElement
		activeElements.push(event.target)

		const triggerDataCurrent = compsToTriggers[target.id]?.focus
		if (triggerDataCurrent) {
			// apply effect when focused element is the trigger component
			reactionCreatorFactory.handleReaction(event, triggerDataCurrent, target.id, false)
		} else {
			// apply effect when focused element is a descendant of a trigger component
			Object.entries(compsToTriggers).forEach(([srcComp, triggerData]) => {
				if (triggerData.focus) {
					const itemId = getItemId(target.id)
					const compId = itemId ? `${srcComp}${REPEATER_DELIMITER}${itemId}` : srcComp
					const compElement = browserWindow!.document.getElementById(compId)
					if (compElement && compElement!.contains(event.target as HTMLElement)) {
						reactionCreatorFactory.handleReaction(event, triggerData.focus, target.id, false)
					}
				}
			})
		}

		// remove effects from non active elements
		const nonActiveElements = activeElements.filter((activeElement) => {
			const isFocusedElementContainedInActiveElement = activeElement.contains(event.target)
			if (experiments['specs.thunderbolt.propagate_event_mobile_hover_interaction'] && lightboxAPI?.isOpen()) {
				// don't reverse effects for elements in page beneath lightbox
				const currentLightboxId = lightboxAPI?.getCurrentLightboxId()
				const lightboxElement = browserWindow!.document.getElementById(currentLightboxId!)
				const isElementUnderLightbox = lightboxElement!.contains(activeElement)
				return isElementUnderLightbox && !isFocusedElementContainedInActiveElement
			}

			return !isFocusedElementContainedInActiveElement
		})
		nonActiveElements.forEach((element) => {
			const triggerDataToRemove = compsToTriggers[getFullId(element.id)]?.focus
			if (triggerDataToRemove) {
				reactionCreatorFactory.handleReaction(event, triggerDataToRemove, element.id, true)
			}
			activeElements = _.remove(activeElements, (e) => !(e.id === element.id))
		})
	}

	const keyDownHandler = (event: KeyboardEvent) => {
		if ((event.code === 'Space' || event.key === 'Enter') && event.target) {
			const target = event.target as HTMLElement
			const triggerData = compsToTriggers[getFullId(target.id)]?.keydown
			if (triggerData) {
				reactionCreatorFactory.handleReaction(event, triggerData, target.id, false)
			}
		}
	}

	let pageVisibleResolver: (value?: unknown) => void
	const pageVisiblePromise = new Promise((resolve) => (pageVisibleResolver = resolve))
	const registerPageVisibleTrigger = ({ pageId, triggerData }: RegisterTriggerFunctionParams) => {
		pageVisiblePromise.then(reactionCreatorFactory.createReaction(triggerData, pageId))
	}

	const triggerTypeRegisterHandler: { [index: string]: Function } = {
		click: registerCommonTrigger,
		tap: registerCommonTrigger,
		'mouse-in': registerCommonTrigger,
		'mouse-out': registerCommonTrigger,
		hover: registerHoverTrigger,
		'animation-start': registerAnimationTrigger,
		'animation-end': registerAnimationTrigger,
		'page-visible': registerPageVisibleTrigger,
	}

	const addCssClassToClickTrigger = ({ compToRegister, triggerData }: RegisterTriggerFunctionParams) => {
		Object.keys(triggerData).forEach((originalTarget) => {
			const tuples = triggerData[originalTarget]
			if (tuples.some((tuple) => isTriggerBpRangeInCurrentBreakpoint(tuple))) {
				const element = browserWindow.document.getElementById(compToRegister)
				if (!element) {
					return
				}
				element.classList.add('has-click-trigger')
			}
		})
	}

	const triggerTypeOnDidMountHandler: { [index: string]: Function } = {
		click: addCssClassToClickTrigger,
	}

	const registerCompsByTriggerMap = (triggerToRegistryMap: { [triggerType: string]: Function }, pageId: string) => {
		Object.entries(compsToTriggers).forEach(([compToRegister, compTriggerData]) => {
			Object.entries(compTriggerData).forEach(([triggerType, triggerData]) => {
				triggerToRegistryMap[triggerType]?.({ compToRegister, triggerData, triggerType, pageId })
			})
		})
	}

	return {
		name: 'triggersAndReactions',
		async pageWillMount(pageId) {
			compIdsWithAccessibleTrigger.forEach((compId) => {
				const a11yProps = props.get(compId).a11y || {}
				props.update({
					[compId]: { a11y: { tabIndex: 0, ...a11yProps } },
				})
			})
			if (shouldEnableTriggersAndReactions) {
				if (motionEffects) {
					motionEffectsManager = await motionEffects.getInstance()
				}

				// register triggers
				registerCompsByTriggerMap(triggerTypeRegisterHandler, pageId)
			}
		},
		pageDidMount(pageId) {
			if (shouldEnableTriggersAndReactions) {
				if (pageIdSymbol !== 'masterPage' && shouldEnableAccessibilityTriggers) {
					browserWindow.addEventListener('focusin', focusInHandler)
					browserWindow.addEventListener('keydown', keyDownHandler)
				}

				if (motionEffectsManager) {
					scrubEffectsManager.init()
				}

				viewportIntersectionHandler.init()

				registerCompsByTriggerMap(triggerTypeOnDidMountHandler, pageId)
			}
		},
		appDidLoadPage() {
			pageVisibleResolver()
		},
		pageDidUnmount() {
			if (shouldEnableTriggersAndReactions) {
				if (shouldEnableAccessibilityTriggers) {
					browserWindow.removeEventListener('keydown', keyDownHandler)
					browserWindow.removeEventListener('focusin', focusInHandler)
				}
				viewportIntersectionHandler.destroy()
			}
		},
		pageWillUnmount() {
			if (shouldEnableTriggersAndReactions) {
				scrubEffectsManager.destroy()

				Object.keys(effectTriggerToReaction).forEach((effectId) => {
					motionEffectsManager?.clearEffectCallbacks(effectId)
				})
			}
		},
	}
}

export const TriggersAndReactions = withDependencies(
	[
		named(PageFeatureConfigSymbol, featureName),
		ReactionCreatorFactorySymbol,
		CompEventsRegistrarSym,
		BrowserWindowSymbol,
		pageIdSym,
		Props,
		ViewportIntersectionHandlerSymbol,
		triggersBreakpointValidatorSymbol,
		scrubReactionsManagerSymbol,
		ShouldEnableTriggersAndReactionsSymbol,
		ExperimentsSymbol,
		optional(LightboxSymbol),
		optional(MotionEffectsInitSymbol),
	],
	triggersAndReactionsFactory
)
